import type { ParsedStatement, ParsedTransaction } from "@paisa/types"

// ── Column x-boundaries ──────────────────────────────────────────────────
// Derived from Amex India PDF coordinate analysis.
const X_DATE_MAX = 20 // Date: x ≤ 20 (at x≈14), format "March 9"
const X_DESC_MIN = 80 // Description: 80 ≤ x < 340 (merchant + city)
const X_DESC_MAX = 340
const X_AMOUNT_MIN = 465 // Amount + optional "CR" indicator: x ≥ 465

// ── Y-coordinate boundaries ───────────────────────────────────────────────
// Skip the payment advice block that appears at the bottom of every page.
const Y_DATA_MIN = 260

// Amex "CR" indicator appears 1–11 y-units below its transaction row.
// Using 12 so it merges into the correct row; the minimum gap between
// consecutive transaction rows is ~18 y-units, so no false merges occur.
const ROW_TOLERANCE = 12

// ── Detect ───────────────────────────────────────────────────────────────

/**
 * Detect whether raw text extracted from a PDF looks like an Amex credit card statement.
 */
export function isAmexCreditCardPdf(rawContent: string): boolean {
  return (
    /American Express/i.test(rawContent) &&
    /Statement of Account/i.test(rawContent) &&
    /XXXX-XXXXXX-\d+/i.test(rawContent)
  )
}

// ── Parse ────────────────────────────────────────────────────────────────

/**
 * Parse an American Express India credit card PDF statement.
 *
 * @param data      Raw PDF file as ArrayBuffer
 * @param password  Optional PDF password
 */
export function parseAmexCreditCardItems(items: Coord[]): ParsedStatement {
  const metadata = extractMetadata(items)
  const transactions = parseTransactions(items, metadata)

  return {
    bank: "amex",
    accountType: "credit_card",
    accountNumber: metadata.accountLast4,
    periodStart: metadata.periodStart,
    periodEnd: metadata.periodEnd,
    transactions,
  }
}

// ── Text extraction ───────────────────────────────────────────────────────

export interface Coord {
  page: number
  x: number
  y: number
  str: string
}

// ── Metadata extraction ───────────────────────────────────────────────────

interface StatementMetadata {
  accountLast4: string | null
  periodStart: string | null
  periodEnd: string | null
  periodEndYear: number
  periodEndMonth: number
}

function extractMetadata(items: Coord[]): StatementMetadata {
  // Metadata is repeated on every page; reading page 1 is enough.
  const page1Items = items.filter((i) => i.page === 1)
  const rowStrings = groupIntoRowStrings(page1Items)
  const fullText = rowStrings.join(" ")

  let accountLast4: string | null = null
  let periodStart: string | null = null
  let periodEnd: string | null = null
  let periodEndYear = new Date().getFullYear()
  let periodEndMonth = 1

  // "XXXX-XXXXXX-73000" → last 4 = "3000"
  const accountMatch = fullText.match(/XXXX-XXXXXX-(\d+)/)
  if (accountMatch) {
    accountLast4 = accountMatch[1].slice(-4)
  }

  // "From February 26 to March 25, 2026"
  const periodMatch = fullText.match(/From\s+(\w+\s+\d+)\s+to\s+(\w+\s+\d+,?\s*\d{4})/i)
  if (periodMatch) {
    const endParsed = parseMonthDayYear(periodMatch[2])
    if (endParsed) {
      periodEnd = endParsed.iso
      periodEndYear = endParsed.year
      periodEndMonth = endParsed.month

      // Start date has no explicit year — infer from end year.
      // If start month > end month the statement spans a year boundary.
      const startParsed = parseMonthDay(periodMatch[1])
      if (startParsed) {
        const startYear = startParsed.month > periodEndMonth ? periodEndYear - 1 : periodEndYear
        periodStart = `${startYear}-${String(startParsed.month).padStart(2, "0")}-${String(startParsed.day).padStart(2, "0")}`
      }
    }
  }

  return { accountLast4, periodStart, periodEnd, periodEndYear, periodEndMonth }
}

function groupIntoRowStrings(items: Coord[]): string[] {
  const buckets = new Map<string, Coord[]>()

  for (const item of items) {
    const pagePrefix = `${item.page}:`
    let bucketKey: string | null = null
    for (const key of buckets.keys()) {
      if (!key.startsWith(pagePrefix)) continue
      if (Math.abs(item.y - parseInt(key.slice(pagePrefix.length), 10)) <= ROW_TOLERANCE) {
        bucketKey = key
        break
      }
    }
    if (bucketKey === null) {
      bucketKey = `${item.page}:${item.y}`
      buckets.set(bucketKey, [item])
    } else {
      const existing = buckets.get(bucketKey)
      if (existing) existing.push(item)
    }
  }

  return [...buckets.values()].map((row) =>
    row
      .sort((a, b) => a.x - b.x)
      .map((c) => c.str)
      .join(" "),
  )
}

// ── Transaction parsing ───────────────────────────────────────────────────

function parseTransactions(items: Coord[], metadata: StatementMetadata): ParsedTransaction[] {
  // Drop payment advice footer and page header regions
  const dataItems = items.filter((i) => i.y > Y_DATA_MIN)

  const rows = buildRows(dataItems)
  const transactions: ParsedTransaction[] = []

  for (const row of rows) {
    const dateCell = row.find((c) => c.x <= X_DATE_MAX)
    if (!dateCell) continue

    // Must look like "Month Day" — e.g. "March 9", "February 26"
    const date = parseAmexPdfDate(dateCell.str.trim(), metadata)
    if (!date) continue

    const descCells = row.filter((c) => c.x >= X_DESC_MIN && c.x < X_DESC_MAX)
    if (descCells.length === 0) continue
    const description = descCells
      .sort((a, b) => a.x - b.x)
      .map((c) => c.str)
      .join(" ")
      .replace(/\s{2,}/g, " ")
      .trim()
    if (!description) continue

    const amountCells = row.filter((c) => c.x >= X_AMOUNT_MIN)
    const isCredit = amountCells.some((c) => c.str.trim() === "CR")
    const amountCell = amountCells.find((c) => c.str.trim() !== "CR")
    if (!amountCell) continue

    const amount = parseAmount(amountCell.str)
    if (amount === 0) continue

    const type: "debit" | "credit" = isCredit ? "credit" : "debit"

    transactions.push({
      date,
      description,
      amount,
      type,
      referenceNumber: null,
      valueDate: null,
      closingBalance: null,
      paymentMethod: null,
      extractedName: description,
      upiId: null,
      sourceCategory: null,
    })
  }

  return transactions
}

/**
 * Group text items into rows using composite "page:y" keys.
 * Uses ROW_TOLERANCE=12 so the "CR" indicator (up to 11 y-units below
 * its amount) merges into the correct transaction row.
 * Sorted document-order: page asc, y desc (top → bottom).
 */
function buildRows(items: Coord[]): Coord[][] {
  const buckets = new Map<string, Coord[]>()

  for (const item of items) {
    const pagePrefix = `${item.page}:`
    let bucketKey: string | null = null

    for (const key of buckets.keys()) {
      if (!key.startsWith(pagePrefix)) continue
      const bucketY = parseInt(key.slice(pagePrefix.length), 10)
      if (Math.abs(item.y - bucketY) <= ROW_TOLERANCE) {
        bucketKey = key
        break
      }
    }

    if (bucketKey === null) {
      bucketKey = `${item.page}:${item.y}`
      buckets.set(bucketKey, [item])
    } else {
      const existing = buckets.get(bucketKey)
      if (existing) existing.push(item)
    }
  }

  return [...buckets.entries()]
    .sort((a, b) => {
      const [pa, ya] = a[0].split(":").map(Number)
      const [pb, yb] = b[0].split(":").map(Number)
      return pa !== pb ? pa - pb : yb - ya
    })
    .map(([, row]) => row.sort((r1, r2) => r1.x - r2.x))
}

// ── Utilities ────────────────────────────────────────────────────────────

const MONTH_MAP: Record<string, number> = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
}

interface MonthDay {
  month: number
  day: number
}

interface MonthDayYear extends MonthDay {
  year: number
  iso: string
}

/** Parse "March 25, 2026" or "March 25 2026" → { month, day, year, iso } */
function parseMonthDayYear(raw: string): MonthDayYear | null {
  const match = raw.trim().match(/^(\w+)\s+(\d{1,2}),?\s+(\d{4})$/)
  if (!match) return null
  const month = MONTH_MAP[match[1].toLowerCase()]
  if (!month) return null
  const day = parseInt(match[2], 10)
  const year = parseInt(match[3], 10)
  const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
  return { month, day, year, iso }
}

/** Parse "February 26" (no year) → { month, day } */
function parseMonthDay(raw: string): MonthDay | null {
  const match = raw.trim().match(/^(\w+)\s+(\d{1,2})$/)
  if (!match) return null
  const month = MONTH_MAP[match[1].toLowerCase()]
  if (!month) return null
  return { month, day: parseInt(match[2], 10) }
}

/**
 * Parse a transaction date like "March 9" using the statement period for the year.
 * If the transaction month is after the statement end month, it belongs to the prior year
 * (e.g., a Dec→Jan statement where the statement end year is the January year).
 */
function parseAmexPdfDate(raw: string, meta: StatementMetadata): string | null {
  const parsed = parseMonthDay(raw)
  if (!parsed) return null

  const year = parsed.month > meta.periodEndMonth ? meta.periodEndYear - 1 : meta.periodEndYear

  return `${year}-${String(parsed.month).padStart(2, "0")}-${String(parsed.day).padStart(2, "0")}`
}

function parseAmount(raw: string | null | undefined): number {
  if (!raw) return 0
  const cleaned = raw.replace(/,/g, "").trim()
  const num = parseFloat(cleaned)
  return Number.isNaN(num) ? 0 : Math.abs(num)
}
